import { useAppContext } from 'Context/ReactAppContext';
import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { getB2BPaymentProviderLabel as _getB2BPaymentProviderLabel } from './B2BPaymentProvidersUtils';

type B2BPaymentViewContextType = {
  getB2BPaymentProviderLabel: (params: {
    paymentProvider: PaymentProvider;
    isTotalAmount: boolean;
  }) => string | false;
};

const B2BPaymentViewContext = createContext<B2BPaymentViewContextType>({
  getB2BPaymentProviderLabel: () => false,
});

type B2BPaymentViewProviderProps = {
  children: ReactNode;
  paymentProviders?: ReadonlyArray<PaymentProvider>;
  paymentSchedule?: PaymentSchedule;
  /** Legacy spec prop — ignored at runtime. */
  withB2B?: boolean;
};

export const B2BPaymentViewProvider = ({ children }: B2BPaymentViewProviderProps) => {
  const appContext = useAppContext();
  const { isCxo, isCmta } = appContext;

  const getLabelByCanal = ({ cxoLabel, cmtaLabel }: { cxoLabel: string; cmtaLabel: string }) => {
    if (isCmta) {
      return cmtaLabel;
    }
    if (isCxo) {
      return cxoLabel;
    }
    return cxoLabel;
  };

  const { isSeller } = appContext;

  const getB2BPaymentProviderLabel = ({
    paymentProvider,
    isTotalAmount,
  }: {
    paymentProvider: PaymentProvider;
    isTotalAmount: boolean;
  }) =>
    isSeller && _getB2BPaymentProviderLabel({ paymentProvider, isTotalAmount, getLabelByCanal });

  return (
    <B2BPaymentViewContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        getB2BPaymentProviderLabel,
      }}
    >
      {children}
    </B2BPaymentViewContext.Provider>
  );
};

export const useB2BPaymentViewContext = () => {
  return useContext(B2BPaymentViewContext);
};
